import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Available languages - add new languages here
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', nativeName: 'English', flag: 'gb' },
  de: { name: 'German', nativeName: 'Deutsch', flag: 'de' },
  es: { name: 'Spanish', nativeName: 'Español', flag: 'es' },
  fr: { name: 'French', nativeName: 'Français', flag: 'fr' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// Namespaces - one file per surface
export const NAMESPACES = [
  'landing',     // Landing page
  'app',         // Main app (includes common, auth, booking, chat, profile)
  'help',        // Help center
  'events',      // Events index (discovery feed + Kiki banner)
] as const;

export type Namespace = (typeof NAMESPACES)[number];

// Default namespace used when none is specified
export const DEFAULT_NAMESPACE: Namespace = 'app';

i18n
  // Load translations via HTTP (lazy loading)
  .use(HttpBackend)
  // Detect user language from browser/localStorage
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  .init({
    // Fallback language when translation is missing
    fallbackLng: 'en',

    // Only these language codes are real. Without this, a `de-DE` browser sends
    // i18next hunting for /locales/de-DE/app.json (a 404 on the critical path,
    // behind the full-screen Suspense loader) and leaves i18n.language as
    // 'de-DE', which is absent from SUPPORTED_LANGUAGES — so LanguageSelector
    // fell back to showing "English" while the UI rendered German.
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

    // Strip the region: de-DE -> de, fr-CA -> fr.
    load: 'languageOnly',

    // Default namespace
    defaultNS: DEFAULT_NAMESPACE,
    
    // Namespaces to preload
    ns: ['app'],
    
    // Enable debug in development
    debug: import.meta.env.DEV,
    
    // Interpolation settings
    interpolation: {
      // React already escapes values
      escapeValue: false,
    },
    
    // HTTP Backend configuration for lazy loading
    backend: {
      // Path to translation files
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    
    // Language detection configuration
    detection: {
      // Order of language detection methods
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Cache language in localStorage
      caches: ['localStorage'],
      // localStorage key
      lookupLocalStorage: 'i18nextLng',
    },
    
    // React specific options
    react: {
      // Suspense mode for loading states
      useSuspense: true,
    },
  });

// Re-export utilities for convenience
export { useT } from './useT';
export { Trans } from './Trans';
export { TranslationProvider } from './TranslationProvider';
export { LanguageSelector } from './LanguageSelector';
export { LanguageSyncInitializer } from './LanguageSyncInitializer';
export { useLanguageSync, markLanguageSourceManual } from './useLanguageSync';

export default i18n;

